'use client'

import type { EligibilityProps } from './Eligibility'

import '@/views/personal/financing/styles/CashlinePersonal.css'

import { Col, Container, Row } from 'react-bootstrap'

import { useIsMobile } from '@/hooks/useIsMobile'

import './Eligibility.css'

import { Fragment } from 'react/jsx-runtime'

const Eligibility = ({
  title = 'Apply easily',
  highlightedText = 'in just\n2 steps',
  documentsTitle = 'Required Documents',
  documents = [
    {
      text: 'Latest <b>EPF statement</b>',
      backgroundColor: '#DCE9FF'
    },
    {
      text: 'Latest <b>3 months salary slip</b> and personal bank statement reflecting salary crediting',
      backgroundColor: '#F2F4F7'
    }
  ],
  employeeType = '',
  tagline2 = '',
  eligibilityTitle = 'Eligibility',
  ageRange = {
    minimum: {
      value: '21',
      label: 'Minimum Age'
    },
    maximum: {
      value: '35',
      label: 'Maximum Age'
    }
  },
  income = {
    range: 'RM 3,000 - RM 4,999',
    description: 'of minimum monthly gross income'
  }
}: EligibilityProps) => {
  // useIsMobile (mounted-gated), not raw useMediaQuery: the static export
  // bakes isMobile=false into the HTML, and the raw hook can stay stuck on
  // that value after hydration, leaving the desktop row layout on phones.
  const isMobile = useIsMobile({ maxWidth: 767 })
  return (
    <section id="eligibility" className="values section h-100">
      <Container>
        <Row className="gy-4">
          <Col
            xl={5}
            xs={12}
            className="d-flex justify-content-end flex-column"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <p
              data-aos="fade-up"
              className="requirements-text"
              style={{
                marginBottom: '16px'
              }}
            >
              <span>{title}</span>{' '}
              <span style={{ color: '#221AFB' }}>
                {highlightedText.split('\n').map((text, i) => (
                  <Fragment key={i}>
                    {text}
                    {i === 0 && <br />}
                  </Fragment>
                ))}
              </span>
            </p>

            <div
              className="d-flex flex-wrap order-xs-2 order-xl-1"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="docs-required-header">{documentsTitle}</div>

              <div
                className="docs-required-content d-flex align-items-center"
                style={{
                  borderBottomLeftRadius: '12px',
                  borderBottomRightRadius: '12px',
                  overflow: 'hidden'
                }}
              >
                <div className="row w-100">
                  {documents.length > 1
                    ? documents.map((doc, index) => (
                        <div
                          key={index}
                          className={`col-md-6 ${index === 0 ? 'docs-subtext' : 'docs-subtext-2'}`}
                          style={{
                            backgroundColor: doc.backgroundColor
                          }}
                        >
                          {doc.title && (
                            <div
                              style={{
                                fontSize: '18px',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                marginTop: '0'
                              }}
                              dangerouslySetInnerHTML={{ __html: doc.title }}
                            />
                          )}

                          <div
                            style={{
                              display: 'flex',
                              // justifyContent: "center", // Center the subtext
                              alignItems: 'center', // Center vertically if necessary
                              height: '80%' // Ensure it takes up the full height available
                            }}
                          >
                            <ul>
                              <li dangerouslySetInnerHTML={{ __html: doc.text ?? '' }} />
                            </ul>
                          </div>
                        </div>
                      ))
                    : documents.map((doc, index) => (
                        <div
                          key={index}
                          className={`docs-subtext ${
                            index === 0 ? 'docs-subtext' : 'docs-subtext-2'
                          }`}
                          style={{
                            backgroundColor: doc.backgroundColor
                          }}
                        >
                          <div
                            style={{
                              fontSize: '18px',
                              display: 'flex',
                              justifyContent: 'flex-start',
                              marginTop: '0'
                            }}
                            dangerouslySetInnerHTML={{ __html: doc.text ?? '' }}
                          />
                        </div>
                      ))}
                </div>
              </div>
            </div>
          </Col>

          <Col
            xl={7}
            xs={12}
            className="order-xs-1 order-xl-2 eligibility-main-container"
            data-aos="fade-left"
            data-aos-delay="300"
          >
            <div className="docs-required-header text-start px-5">{eligibilityTitle}</div>

            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between'
              }}
            >
              {/* First Section like Age section */}

              <div
                className="eligibility-content"
                style={{
                  flex: 1,
                  borderBottomLeftRadius: '12px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  backgroundColor: '#DCE9FF'
                }}
              >
                {/* Top Section */}
                {employeeType && (
                  <div className="eligibility-subheader text-center">Eligibility</div>
                )}

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '32px'
                  }}
                >
                  {/* ROW 1 — Salaried */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <span style={{ fontSize: '56px', fontWeight: 700 }}>
                        {ageRange.minimum?.value}
                      </span>

                      <span
                        style={{
                          fontSize: '40px',
                          fontWeight: 600,
                          lineHeight: 1
                        }}
                      >
                        –
                      </span>

                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: '6px'
                        }}
                      >
                        <span style={{ fontSize: '56px', fontWeight: 700 }}>
                          {ageRange.maximum?.value}
                        </span>
                        <span style={{ fontSize: '20px' }}>years old</span>
                      </span>
                    </div>

                    <div
                      style={{
                        marginTop: '12px',
                        textAlign: 'center',
                        fontSize: '16px',
                        color: '#000045',
                        fontWeight: 600
                      }}
                    >
                      Salaried, up to retirement age
                    </div>
                  </div>

                  {/* DIVIDER LINE */}
                  <div
                    style={{
                      width: '100%',
                      height: '1px',
                      backgroundColor: '#98A2B3'
                    }}
                  />

                  {/* ROW 2 — Professional self‑employed */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <span style={{ fontSize: '20px', fontWeight: 500 }}>up to</span>

                      <span style={{ fontSize: '56px', fontWeight: 700, marginBottom: '16px' }}>
                        65
                      </span>

                      <span style={{ fontSize: '20px' }}>years old</span>
                    </div>

                    <div>
                      <p
                        style={{
                          textAlign: 'center',
                          fontSize: '16px',
                          color: '#000045'
                        }}
                      >
                        Professional self‑employed
                      </p>

                      <p
                        style={{
                          textAlign: 'center',
                          fontSize: '14px',
                          color: '#72788E',
                          marginTop: '-18px'
                        }}
                      >
                        (limited to medical doctors and dentists attached to hospitals)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Second Section */}
              <div
                className="eligibility-content"
                style={{
                  flex: 1,
                  borderBottomRightRadius: '12px',
                  backgroundColor: '#f1f1f1',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  padding: '20px'
                }}
              >
                {tagline2 && <div className="eligibilty-subheader-2">{tagline2}</div>}

                <div
                  className="eligibilty-income"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <span
                    style={{
                      fontSize: '56px',
                      fontWeight: 700
                    }}
                  >
                    {income.range}
                  </span>

                  <div
                    className="eligibility_income"
                    style={{
                      fontSize: '16px',
                      color: '#555'
                    }}
                  >
                    {income.description}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  )
}

export default Eligibility
